/**
 * Group display-name registry — single source of truth for how component
 * group names render on each surface. Add new entries here without touching
 * the backend `data.py` seed file.
 *
 * Usage:
 *   import { getGroupLabel } from '../lib/groupLabels';
 *   const label = getGroupLabel('Transition, Go-Live, Hypercare', 'stepper');
 *
 * Known surfaces:
 *   - stepper  : Estimator mini-wizard step labels (tight horizontal space)
 *   - short    : small badges, chips, dropdown options
 *   - gantt    : Planner Gantt row labels
 *   - table    : breakdown / summary tables
 *   - export   : PDF / Word / PPTX / Excel exports (prefer full formal name)
 *   - default  : fallback — the literal group name from the backend
 *
 * If a group or surface is missing, the helper falls back to the group's
 * literal name (also used in the backend / MongoDB).
 */

// Special pseudo-groups that appear as extra stepper tabs (not real component groups)
const PSEUDO_GROUPS = {
  'project-factors': { default: 'Project Factors' },
  'new-development': { default: 'New Development' },
  'integration-scope': { default: 'Integration Scope' },
};

const GROUP_LABELS = {
  'Transition, Go-Live, Hypercare': {
    stepper: 'Transition & Go-Live',
    short: 'Transition & Go-Live',
    gantt: 'Transition & Go-Live',
  },
  'Consulting & Delivery': {
    short: 'Consulting',
  },
  'Core Configuration': {
    short: 'Core Config',
  },
  'Extended Configuration': {
    short: 'Extended Config',
  },
  'User Experience': {
    short: 'UX',
  },
  'Data Migration': {
    short: 'Data',
  },
  Integrations: {
    short: 'Integrations',
  },
};

// Runtime overrides fetched from the backend (super admin editable).
// Shape: { [groupName]: { stepper?, short?, gantt?, table?, export?, default? } }
// Takes precedence over the static GROUP_LABELS map.
let OVERRIDES = {};

/**
 * Replace the in-memory overrides map. Call once on app boot and again
 * whenever a super admin saves a change.
 * @param {object} next — map of { groupName: { surface: label } }
 */
export function setGroupLabelOverrides(next) {
  OVERRIDES = (next && typeof next === 'object') ? next : {};
}

export function getGroupLabelOverrides() {
  return OVERRIDES;
}

/**
 * Resolve the best display label for a group on the given surface.
 * Lookup order: runtime overrides → static defaults → literal group name.
 * @param {string} group — group key (either a real component group or a pseudo key)
 * @param {string} [surface='default'] — one of 'stepper' | 'short' | 'gantt' | 'table' | 'export' | 'default'
 * @returns {string}
 */
export function getGroupLabel(group, surface = 'default') {
  if (!group) return '';
  const override = OVERRIDES[group];
  if (override && override[surface]) return override[surface];
  if (override && override.default) return override.default;
  const entry = PSEUDO_GROUPS[group] || GROUP_LABELS[group];
  if (!entry) return group;
  return entry[surface] || entry.default || group;
}

export { GROUP_LABELS, PSEUDO_GROUPS };
